import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { IAddress } from "../../types/seed";
import {
  fetchAddressFromCEP,
  formatCEP,
  isValidCEP,
  BRAZILIAN_STATES,
} from "../../utils/addressUtils";
import { useState } from "react";

interface AddressFieldsetProps {
  address: IAddress | null;
  onChange: (address: IAddress | null) => void;
}

const AddressFieldset = ({ address, onChange }: AddressFieldsetProps) => {
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [cepError, setCepError] = useState<string>("");
  const handleFieldChange = (field: keyof IAddress, value: string | number) => {
    const currentAddress = address || {
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "",
      postal_code: "",
      country_code: "BR",
    };

    onChange({
      ...currentAddress,
      [field]: value,
    });
  };

  const handleCEPChange = async (cep: string) => {
    const formattedCep = formatCEP(cep);
    handleFieldChange("postal_code", formattedCep);
    setCepError("");

    // Only fetch if CEP is complete and valid
    if (isValidCEP(cep)) {
      setIsLoadingCEP(true);
      try {
        const addressData = await fetchAddressFromCEP(cep);
        if (addressData) {
          // Update address with fetched data
          const currentAddress = address || {
            street: "",
            number: "",
            complement: "",
            neighborhood: "",
            city: "",
            state: "",
            postal_code: "",
            country_code: "BR",
          };

          const updatedAddress: IAddress = {
            ...currentAddress,
            postal_code: formattedCep,
            street: addressData.street,
            neighborhood: addressData.neighborhood,
            city: addressData.city,
            state: addressData.state,
            country_code: "BR",
          };
          onChange(updatedAddress);
        } else {
          setCepError("CEP não encontrado");
        }
      } catch (error) {
        setCepError("Erro ao buscar CEP");
      } finally {
        setIsLoadingCEP(false);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "0.5rem 0 0.25rem",
      }}
    >
      {/* CEP field first for prefilling */}
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <TextField
          label="CEP"
          value={address?.postal_code || ""}
          onChange={(e) => handleCEPChange(e.target.value)}
          sx={{ minWidth: "150px" }}
          required
          placeholder="00000-000"
          error={!!cepError}
          helperText={cepError}
          InputProps={{
            endAdornment: isLoadingCEP ? <CircularProgress size={20} /> : null,
          }}
        />
      </Box>

      {cepError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {cepError}
        </Alert>
      )}

      {/* Address fields */}
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <TextField
          label="Logradouro"
          value={address?.street || ""}
          onChange={(e) => handleFieldChange("street", e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Número"
          value={address?.number || ""}
          onChange={(e) => handleFieldChange("number", e.target.value)}
          sx={{ minWidth: "120px" }}
          required
        />
      </Box>

      <TextField
        label="Complemento"
        value={address?.complement || ""}
        onChange={(e) => handleFieldChange("complement", e.target.value)}
        placeholder="Apartamento, casa, etc."
      />

      {/* Location fields - rearranged for better state field width */}
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <TextField
          label="Bairro"
          value={address?.neighborhood || ""}
          onChange={(e) => handleFieldChange("neighborhood", e.target.value)}
          sx={{ flex: 1 }}
          required
        />
        <TextField
          label="Cidade"
          value={address?.city || ""}
          onChange={(e) => handleFieldChange("city", e.target.value)}
          sx={{ flex: 1 }}
          required
        />
        <FormControl sx={{ minWidth: "180px" }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={address?.state || ""}
            onChange={(e) => handleFieldChange("state", e.target.value)}
            label="Estado"
            required
          >
            {BRAZILIAN_STATES.map((state) => (
              <MenuItem key={state.code} value={state.code}>
                {state.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default AddressFieldset;
